import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Download, Video, CheckCircle2, Eye, Mic, ChevronDown, ChevronUp, BookOpen } from "lucide-react";
import api, { fetchRecordingBlobUrl } from "../lib/api";
import { getUserId } from "../lib/user";
import { sessionMoment, fmtDateTime, sessionLateness } from "../lib/dates";
import { cadenceLabel, isKickoff } from "../lib/cadence";
import { Button } from "../components/ui/button";
import ShareFirstSessionCard from "../components/ShareFirstSessionCard";
import AskTranscript from "../components/AskTranscript";

function Section({ eyebrow, title, items, empty }) {
  if (!items || items.length === 0) {
    return (
      <section>
        <p className="label-eyebrow">{eyebrow}</p>
        <h3 className="font-serif text-2xl mt-2">{title}</h3>
        <p className="text-sm text-[#666] mt-3">{empty}</p>
      </section>
    );
  }
  return (
    <section>
      <p className="label-eyebrow">{eyebrow}</p>
      <h3 className="font-serif text-2xl mt-2">{title}</h3>
      <ul className="mt-4 space-y-2">
        {items.map((s, i) => {
          const label = typeof s === "string" ? s : (s.title || "");
          const key = typeof s === "string" ? `${i}:${s}` : (s.id || `${i}:${label}`);
          return (
            <li key={key} className="flex gap-3 text-[#ddd]">
              <span className="font-mono text-[#c89f45] text-sm shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span>{label}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function MeetingMinutes() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [meetingActionItems, setMeetingActionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);
  // Blob URL for the recording — fetched via authenticated client, then
  // handed to <video> as an object URL. Revoked on unmount / meeting change
  // so we don't leak memory (blob URLs pin their underlying bytes).
  const [recordingSrc, setRecordingSrc] = useState(null);
  const autoTranscribeTriedRef = useRef(false);
  const autoGenerateTriedRef = useRef(false);

  const load = async () => {
    try {
      // Fetch meeting + persisted action items in parallel. The persisted
      // items carry the real status + completed_at from user sign-offs; the
      // AI-generated ones baked into meeting.minutes are just their titles.
      const [mRes, aRes] = await Promise.all([
        api.get(`/meetings/${id}`),
        api.get(`/action-items?meeting_id=${id}`).catch(() => ({ data: [] })),
      ]);
      setMeeting(mRes.data);
      setMeetingActionItems(aRes.data || []);
    } catch {
      toast.error("Could not load session");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Fetch the recording as a blob → object URL whenever the meeting has one.
  // SECURITY (SEC-003): we can't use `?user=` query-string auth anymore, so
  // <video> can't hit the endpoint directly — the authenticated fetch below
  // does. The blob URL is revoked on unmount / meeting change to release the
  // pinned bytes.
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    if (meeting?.recording_path) {
      fetchRecordingBlobUrl(meeting.id)
        .then((url) => {
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          createdUrl = url;
          setRecordingSrc(url);
        })
        .catch((e) => {
          console.debug("recording blob load failed", e);
          if (!cancelled) setRecordingSrc(null);
        });
    } else {
      setRecordingSrc(null);
    }
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [meeting?.id, meeting?.recording_path]);

  // Fetch share status once per session view — trigger only when the meeting
  // is completed AND the caller is the host (co-hosts don't get the prompt).
  useEffect(() => {
    if (!meeting || meeting.status !== "completed" || meeting.owner_id !== getUserId()) return;
    api.get("/share/status").then((r) => setShareStatus(r.data)).catch((e) => {
      console.debug("share/status probe failed", e);
    });
  }, [meeting]);

  // Auto-generate minutes on first arrival if not yet present (owner only).
  // Guarded by autoGenerateTriedRef so that a persistent server-side failure
  // to set minutes never causes an infinite generate() loop.
  useEffect(() => {
    if (meeting && !meeting.minutes && !generating && meeting.owner_id === getUserId()) {
      if (autoGenerateTriedRef.current) return;
      autoGenerateTriedRef.current = true;
      generate();
    }
  }, [meeting]);

  // Auto-transcribe: once minutes exist AND a recording is on file AND no
  // transcript has been captured yet, silently fire Whisper in the background
  // so the user doesn't have to click "Transcribe & redraft". Plan gate (Free
  // tier gets 402) is swallowed silently — they still have their notes-based
  // minutes and can upgrade whenever.
  useEffect(() => {
    if (!meeting) return;
    if (meeting.owner_id !== getUserId()) return;
    if (!meeting.recording_path) return;
    if (meeting.transcript) return;
    if (transcribing || generating) return;
    if (!meeting.minutes) return; // wait for initial minutes draft to finish
    if (autoTranscribeTriedRef.current) return;
    autoTranscribeTriedRef.current = true;
    autoTranscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.minutes, meeting?.recording_path, meeting?.transcript]);

  /** Goals the secretary filed on the board — say so, and offer a jump. */
  const announceGoals = (goals) => {
    if (!Array.isArray(goals) || goals.length === 0) return;
    toast.success(
      goals.length === 1 ? "A goal was added to your board" : `${goals.length} goals were added to your board`,
      {
        description: goals.map((g) => g.title).join(" · "),
        action: { label: "View board", onClick: () => nav("/goals") },
        duration: 10000,
      }
    );
  };

  const generate = async () => {
    setGenerating(true);
    try {
      api.get(`/meetings/${id}/transcript-progress`).then((r) => setProgress(r.data)).catch(() => {});
      // Deep reviews can outlive the gateway's patience, so drafting runs in
      // the background and we poll until the minutes land.
      await api.post(`/meetings/${id}/minutes/start`);
      const deadline = Date.now() + 6 * 60 * 1000;
      for (;;) {
        await new Promise((r) => setTimeout(r, 4000));
        const fresh = (await api.get(`/meetings/${id}`)).data;
        if (fresh.minutes) {
          setMeeting(fresh);
          toast.success("Minutes drafted by the secretary.");
          announceGoals(fresh.minutes_goals_created);
          return;
        }
        if (fresh.minutes_status === "failed") {
          const detail = fresh.minutes_error;
          const code = typeof detail === "object" ? detail?.code : null;
          const message = typeof detail === "object" ? detail?.message : typeof detail === "string" ? detail : null;
          if (code === "ai_minutes_limit" || code === "plan_required") {
            toast.error(message || "You're out of free AI minutes this month.", {
              action: { label: "Upgrade", onClick: () => (window.location.href = "/pricing") },
              duration: 8000,
            });
          } else {
            // Say what actually went wrong, and offer one tap to try again.
            toast.error(message || "The secretary couldn't draft the minutes.", {
              description: code === "ai_upstream_unavailable" ? "The AI provider was busy — a retry usually works." : undefined,
              action: { label: "Retry", onClick: () => generate() },
              duration: 12000,
            });
          }
          return;
        }
        if (Date.now() > deadline) {
          toast.error("The secretary is taking unusually long. Try redrafting in a moment.");
          return;
        }
      }
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "object" ? detail?.message : typeof detail === "string" ? detail : null;
      toast.error(message || "The secretary couldn't draft the minutes.", {
        action: { label: "Retry", onClick: () => generate() },
        duration: 12000,
      });
    } finally {
      setGenerating(false);
    }
  };

  const transcribeAndRedraft = async () => {
    setTranscribing(true);
    setGenerating(true);
    try {
      const res = await api.post(`/meetings/${id}/transcribe-and-minutes`);
      setMeeting((prev) => ({
        ...prev,
        transcript: res.data.transcript?.text || prev.transcript,
        transcript_obj: res.data.transcript || prev.transcript_obj,
        minutes: res.data.minutes || prev.minutes,
      }));
      setTranscriptOpen(true);
      if (res.data.minutes_error) {
        toast.success("Transcribed.");
        const m = res.data.minutes_error?.detail;
        const msg = (typeof m === "object" && m?.message) ? m.message : "Couldn't redraft minutes — try again in a moment.";
        toast.error(`Transcript saved · ${msg}`);
      } else {
        const sent = res.data.sent_to_partners || 0;
        toast.success(sent > 0
          ? `Transcribed, minutes redrafted, emailed to ${sent} partner${sent === 1 ? "" : "s"}.`
          : "Transcribed and minutes redrafted from the conversation.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.code === "plan_required") {
          toast.error(detail.message, {
            action: { label: "Upgrade", onClick: () => (window.location.href = "/pricing") },
            duration: 8000,
          });
        } else if (detail.code === "ai_upstream_unavailable") {
          toast.error("AI service temporarily unavailable. Try again shortly.");
        } else if (detail.code === "recording_too_large") {
          toast.error("Recording is over Whisper's 25MB limit.");
        } else if (detail.code === "no_recording") {
          toast.error("No recording on this session to transcribe.");
        } else {
          toast.error(detail.message || "Combined action failed");
        }
      } else {
        toast.error("Combined action failed");
      }
    } finally {
      setTranscribing(false);
      setGenerating(false);
    }
  };

  // Silent auto-run of transcribe-and-redraft. Fires once per session view
  // after the initial notes-based minutes exist and no transcript is present.
  // Fails silently on plan/no_recording gates — the user already has readable
  // minutes and we don't want to nag them with upsells or errors here.
  const autoTranscribe = async () => {
    setTranscribing(true);
    try {
      const res = await api.post(`/meetings/${id}/transcribe-and-minutes`);
      setMeeting((prev) => ({
        ...prev,
        transcript: res.data.transcript?.text || prev.transcript,
        transcript_obj: res.data.transcript || prev.transcript_obj,
        minutes: res.data.minutes || prev.minutes,
      }));
      setTranscriptOpen(true);
      toast.success("AI transcribed your recording and redrafted the minutes.");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      // Silent skip for expected non-issues.
      if (code === "plan_required" || code === "no_recording" || code === "recording_too_large") {
        console.debug("auto-transcribe skipped:", code);
        return;
      }
      // For upstream/transient errors, log quietly — user can click the
      // manual button if they want to retry. No annoying error toast here.
      console.debug("auto-transcribe deferred:", e?.response?.data || e);
    } finally {
      setTranscribing(false);
    }
  };

  const transcribe = async () => {
    setTranscribing(true);
    try {
      const res = await api.post(`/meetings/${id}/transcribe`);
      setMeeting((prev) => ({ ...prev, transcript: res.data.text, transcript_obj: res.data }));
      setTranscriptOpen(true);
      toast.success("Recording transcribed.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.code === "plan_required") {
          toast.error(detail.message, {
            action: { label: "Upgrade", onClick: () => (window.location.href = "/pricing") },
            duration: 8000,
          });
        } else if (detail.code === "ai_upstream_unavailable") {
          toast.error("Transcription service is temporarily unavailable. Try again shortly.");
        } else if (detail.code === "recording_too_large") {
          toast.error("Recording is over Whisper's 25MB limit — too long to transcribe in one shot.");
        } else {
          toast.error(detail.message || "Transcription failed");
        }
      } else {
        toast.error("Transcription failed");
      }
    } finally {
      setTranscribing(false);
    }
  };

  const formatTimestamp = (sec) => {
    if (sec == null) return "";
    const s = Math.floor(sec);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center text-[#666]">Loading minutes…</div>;
  }
  if (!meeting) return null;

  const isOwner = meeting.owner_id === getUserId();
  const m = meeting.minutes || {};
  return (
    <div className="space-y-16" data-testid="minutes-root">
      <header className="flex flex-col gap-6">
        <Link to="/meetings" className="text-xs text-[#666] hover:text-[#c89f45] flex items-center gap-1 w-fit" data-testid="back-to-archive">
          <ArrowLeft size={12} /> Archive
        </Link>
        <div>
          <p className="label-eyebrow">Meeting minutes · {cadenceLabel(meeting.cadence)}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">{meeting.title}</h1>
          <p className="font-mono text-xs text-[#666] mt-3">
            {(() => { const w = sessionMoment(meeting); return `${w.label} ${fmtDateTime(w.iso)}`; })()}
            {meeting.duration_seconds ? ` · ${Math.round(meeting.duration_seconds / 60)} min on the record` : ""}
            {sessionLateness(meeting) && (
              <span className="text-[#8f8266]" data-testid="minutes-late-note">
                {` · ${sessionLateness(meeting).label}`}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isOwner ? (
            <Button
              onClick={generate}
              disabled={generating}
              variant="outline"
              className="bg-transparent border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] rounded-md"
              data-testid="regenerate-minutes-button"
            >
              <Sparkles size={14} className="mr-1.5" /> {generating ? "Drafting…" : (meeting.minutes ? "Redraft minutes" : "Generate minutes")}
            </Button>
          ) : (
            <span className="flex items-center gap-2 text-xs text-[#888] font-mono uppercase tracking-widest" data-testid="participant-badge">
              <Eye size={12} /> Viewing as participant · read-only
            </span>
          )}
          {isOwner && meeting.recording_path && meeting.transcript && meeting.minutes && (
            <span className="text-[10px] font-mono text-[#9bd3c4] bg-[#1B3B36] px-2 py-1 rounded uppercase tracking-widest" data-testid="grounded-badge">
              Grounded in transcript
            </span>
          )}
          {meeting.cadence === "quarterly" && meeting.minutes && (
            <Link
              to={`/meetings/${meeting.id}/board-book`}
              className="inline-flex items-center gap-1.5 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-3 py-2 text-sm transition-colors"
              data-testid="open-board-book-btn"
            >
              <BookOpen size={14} /> Board Book
            </Link>
          )}
        </div>
      </header>

      <div className="gold-divider" />

      {shareStatus?.should_prompt && meeting.minutes && (
        <ShareFirstSessionCard
          sessionsCompleted={shareStatus.sessions_completed}
          hostName={shareStatus.host_name}
          onDismiss={() => setShareStatus((s) => ({ ...s, should_prompt: false }))}
        />
      )}

      {meeting.next_session_id && (
        <Link to={`/meetings/${meeting.next_session_id}`} className="block surface surface-hover rounded-md p-5 transition-colors" data-testid="next-session-banner">
          <p className="label-eyebrow text-[#c89f45]">Next session</p>
          <p className="font-serif text-lg mt-2">A follow-up {meeting.cadence} session has been scheduled — open it to continue the chain.</p>
        </Link>
      )}

      {generating && !meeting.minutes ? (
        <div className="surface rounded-md p-12 text-center" data-testid="minutes-generating">
          <Sparkles size={20} className="mx-auto text-[#c89f45] mb-3 animate-pulse" />
          <p className="font-serif text-2xl">The secretary is drafting the minutes.</p>
          <p className="text-sm text-[#666] mt-2">Distilling wins, losses, goals, challenges, and resolutions.</p>
          {progress?.segments > 0 && (
            <p className="text-xs font-mono uppercase tracking-widest text-[#c89f45] mt-5" data-testid="transcript-progress">
              {Math.round(progress.segments * 2)} min of talk transcribed live · {progress.segments} clip{progress.segments === 1 ? "" : "s"}
            </p>
          )}
        </div>
      ) : !meeting.minutes ? (
        <div className="surface rounded-md p-12 text-center" data-testid="minutes-empty">
          <p className="font-serif text-2xl">{isOwner ? "No minutes drafted yet." : "The chair hasn't published minutes yet."}</p>
          <p className="text-sm text-[#666] mt-2">{isOwner ? "Generate the minutes to convert this session into an action plan." : "Check back once the host has finalized the session."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7 space-y-12">
            {m.executive_summary && (
              <section>
                <p className="label-eyebrow">Executive summary</p>
                <p className="font-serif text-2xl sm:text-3xl mt-4 leading-snug text-[#fafafa]">
                  {m.executive_summary}
                </p>
              </section>
            )}

            {/* Kickoff charter — what the chair is going after this period. */}
            {m.focus_theme && (
              <section data-testid="minutes-focus-theme">
                <p className="label-eyebrow">Theme</p>
                <p className="font-serif text-2xl mt-3 text-[#c89f45]">{m.focus_theme}</p>
              </section>
            )}
            {Array.isArray(m.commitments) && m.commitments.length > 0 && (
              <section data-testid="minutes-commitments">
                <p className="label-eyebrow">Commitments</p>
                <h3 className="font-serif text-2xl mt-2">On the record</h3>
                <ol className="mt-5 space-y-3">
                  {m.commitments.map((c, i) => (
                    <li key={i} className="surface rounded-md p-4 flex gap-4" data-testid={`minutes-commitment-${i}`}>
                      <span className="text-[#c89f45] font-serif text-lg leading-none shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <p className="font-serif text-lg text-[#fafafa]">{c.title || String(c)}</p>
                        {c.description && <p className="text-sm text-[#bbb] mt-1.5 leading-relaxed">{c.description}</p>}
                        {c.measure && (
                          <p className="text-xs font-mono text-[#8a8a8a] mt-2">MEASURE · {c.measure}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}
            {m.priority_one && (m.priority_one.title || typeof m.priority_one === "string") && (
              <section data-testid="minutes-priority-one">
                <p className="label-eyebrow">The one that matters most</p>
                <p className="font-serif text-xl mt-3 text-[#fafafa]">
                  {m.priority_one.title || m.priority_one}
                </p>
                {m.priority_one.why && (
                  <p className="text-sm text-[#bbb] mt-2 leading-relaxed">{m.priority_one.why}</p>
                )}
              </section>
            )}
            {Array.isArray(m.habits) && m.habits.length > 0 && (
              <Section eyebrow="Habits" title="The behaviours committed to" items={m.habits} empty="" />
            )}
            {Array.isArray(m.risks) && m.risks.length > 0 && (
              <Section eyebrow="Risks" title="What could derail this" items={m.risks} empty="" />
            )}
            {Array.isArray(m.guardrails) && m.guardrails.length > 0 && (
              <Section eyebrow="Guardrails" title="What gets protected — and refused" items={m.guardrails} empty="" />
            )}

            {/* Review scorecard — every kickoff commitment, judged. */}
            {Array.isArray(m.commitment_scorecard) && m.commitment_scorecard.length > 0 && (
              <section data-testid="minutes-commitment-scorecard">
                <p className="label-eyebrow">Promises kept</p>
                <h3 className="font-serif text-2xl mt-2">Scored against your kickoff</h3>
                <ul className="mt-5 space-y-3">
                  {m.commitment_scorecard.map((row, i) => {
                    const verdict = (row.verdict || "").toLowerCase();
                    const tone = verdict === "kept"
                      ? "border-[#4a7f52] text-[#8fd39b]"
                      : verdict === "partial"
                        ? "border-[#c89f45] text-[#c89f45]"
                        : "border-[#7f4a4a] text-[#e08c8c]";
                    return (
                      <li key={i} className="surface rounded-md p-4" data-testid={`minutes-scorecard-${i}`}>
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-serif text-lg text-[#fafafa] min-w-0">{row.commitment}</p>
                          <span className={`shrink-0 text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-1 ${tone}`}>
                            {verdict || "unscored"}
                          </span>
                        </div>
                        {row.evidence && (
                          <p className="text-sm text-[#bbb] mt-2 leading-relaxed">{row.evidence}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
            {m.intent_vs_reality && (
              <section data-testid="minutes-intent-vs-reality">
                <p className="label-eyebrow">Intent vs reality</p>
                <h3 className="font-serif text-2xl mt-2">The distance between the two</h3>
                <p className="mt-4 text-[#ddd] leading-relaxed">{m.intent_vs_reality}</p>
              </section>
            )}
            {m.reflection && (
              <section data-testid="minutes-reflection">
                <p className="label-eyebrow">Reflection</p>
                <h3 className="font-serif text-2xl mt-2">The honest read</h3>
                <p className="mt-4 text-[#ddd] leading-relaxed">{m.reflection}</p>
              </section>
            )}
            {!isKickoff(meeting.cadence) && (
              <>
                <Section eyebrow="Wins" title="What worked" items={m.wins} empty="No wins captured." />
                <Section eyebrow="Lessons" title="What was learned" items={m.lessons} empty="No lessons recorded." />
                <Section eyebrow="Goals" title="Progress on goals" items={m.goals_progress} empty="No goals reviewed." />
              </>
            )}

            {/* Monthly-only sections. Only render when the AI actually
                produced the field — keeps weekly minutes unchanged. */}
            {m.financial_snapshot && (
              <section data-testid="minutes-financial-snapshot">
                <p className="label-eyebrow">Financial Snapshot</p>
                <h3 className="font-serif text-2xl mt-2">The money picture</h3>
                <p className="mt-4 text-[#ddd] leading-relaxed">{m.financial_snapshot}</p>
              </section>
            )}
            {Array.isArray(m.metric_trends) && m.metric_trends.length > 0 && (
              <Section eyebrow="Metrics" title="Trend deep-dive" items={m.metric_trends} empty="" />
            )}
            {Array.isArray(m.habit_scorecard) && m.habit_scorecard.length > 0 && (
              <Section eyebrow="Habits" title="Scorecard for the month" items={m.habit_scorecard} empty="" />
            )}

            {/* Quarterly-only: category reflection across the 5 pillars. */}
            {m.category_reflection && typeof m.category_reflection === "object" && (
              <section data-testid="minutes-category-reflection">
                <p className="label-eyebrow">Category Reflection · 5 Pillars</p>
                <h3 className="font-serif text-2xl mt-2">How each pillar held up</h3>
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  {["health", "business", "relationships", "money", "craft"].map((k) => {
                    const val = (m.category_reflection[k] || "").trim();
                    if (!val) return null;
                    return (
                      <div key={k} className="surface rounded-md p-4" data-testid={`minutes-pillar-${k}`}>
                        <p className="label-eyebrow text-[#c89f45]">{k}</p>
                        <p className="text-sm text-[#ddd] mt-2 leading-relaxed">{val}</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {!isKickoff(meeting.cadence) && (
              <Section eyebrow="Challenges" title="What's in the way" items={m.challenges} empty="No challenges raised." />
            )}

            {Array.isArray(m.strategic_shifts) && m.strategic_shifts.length > 0 && (
              <Section eyebrow="Strategy" title="Pivots, doubles-downs, kills" items={m.strategic_shifts} empty="" />
            )}
            {m.big_picture_reflection && (
              <section data-testid="minutes-big-picture">
                <p className="label-eyebrow">Big picture</p>
                <h3 className="font-serif text-2xl mt-2">Is the ship pointed right?</h3>
                <p className="mt-4 text-[#ddd] leading-relaxed">{m.big_picture_reflection}</p>
              </section>
            )}
            {m.vision_alignment && (
              <section data-testid="minutes-vision-alignment">
                <p className="label-eyebrow">Vision Alignment</p>
                <h3 className="font-serif text-2xl mt-2">North Star check</h3>
                <p className="mt-4 text-[#ddd] leading-relaxed">{m.vision_alignment}</p>
              </section>
            )}
            {m.month_pattern_analysis && (
              <section data-testid="minutes-month-pattern">
                <p className="label-eyebrow">Month pattern analysis</p>
                <h3 className="font-serif text-2xl mt-2">What the four weeklies revealed</h3>
                <p className="mt-4 text-[#ddd] leading-relaxed">{m.month_pattern_analysis}</p>
              </section>
            )}
            {m.quarter_pattern_analysis && (
              <section data-testid="minutes-quarter-pattern">
                <p className="label-eyebrow">Quarter pattern analysis</p>
                <h3 className="font-serif text-2xl mt-2">What the three monthlies revealed</h3>
                <p className="mt-4 text-[#ddd] leading-relaxed">{m.quarter_pattern_analysis}</p>
              </section>
            )}

            {/* Quarterly-only: next quarter's bets (richer than plain action items). */}
            {Array.isArray(m.next_quarter_bets) && m.next_quarter_bets.length > 0 && (
              <section data-testid="minutes-next-bets">
                <p className="label-eyebrow">Next Quarter's Bets</p>
                <h3 className="font-serif text-2xl mt-2">Where we're placing the chips</h3>
                <ol className="mt-5 space-y-3">
                  {m.next_quarter_bets.map((bet, i) => (
                    <li
                      key={i}
                      className="surface rounded-md p-4 flex gap-4"
                      data-testid={`minutes-next-bet-${i}`}
                    >
                      <span className="text-[#c89f45] font-serif text-lg leading-none shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <p className="font-serif text-lg text-[#fafafa]">{bet.title}</p>
                        {bet.description && (
                          <p className="text-sm text-[#bbb] mt-1.5 leading-relaxed">{bet.description}</p>
                        )}
                        {bet.why_it_matters && (
                          <p className="text-xs text-[#888] mt-2 leading-relaxed italic">
                            Why it matters — {bet.why_it_matters}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {m.next_focus && (
              <section>
                <p className="label-eyebrow">Next session focus</p>
                <p className="font-serif text-xl mt-3 text-[#c89f45]">{m.next_focus}</p>
              </section>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-8">
            <div className="surface rounded-md p-6" data-testid="minutes-action-items">
              <p className="label-eyebrow">Resolutions · action plan</p>
              <h3 className="font-serif text-2xl mt-2">Signed off</h3>
              {(() => {
                // Prefer persisted action_items (they carry status +
                // completed_at from sign-off). Fall back to the AI-generated
                // titles baked into meeting.minutes for legacy sessions where
                // items weren't persisted, so we don't regress the display.
                const persisted = meetingActionItems.filter(
                  (a) => a.meeting_id === meeting.id,
                );
                const source = persisted.length > 0
                  ? persisted.map((a) => ({
                      id: a.id,
                      title: a.title,
                      description: a.description,
                      status: a.status,
                      completed_at: a.completed_at,
                    }))
                  : (m.action_items || []).map((a, i) => ({
                      id: a.id || `legacy-${i}`,
                      title: a.title,
                      description: a.description,
                      status: null,
                      completed_at: null,
                    }));

                if (source.length === 0) {
                  return <p className="text-sm text-[#666] mt-4">No action items resolved.</p>;
                }
                return (
                  <ul className="mt-4 space-y-3" data-testid="action-items-list">
                    {source.map((a) => {
                      const done = a.status === "done";
                      const stamp = a.completed_at
                        ? new Date(a.completed_at + (a.completed_at.length === 10 ? "T00:00:00" : ""))
                        : null;
                      return (
                        <li
                          key={a.id}
                          className="flex items-start gap-3 border-b border-[#1a1a1a] last:border-0 pb-3 last:pb-0"
                          data-testid={`minutes-action-${a.id}`}
                        >
                          <CheckCircle2
                            size={14}
                            className={`${done ? "text-[#c89f45]" : "text-[#3a3a3a]"} mt-1 shrink-0`}
                          />
                          <div className="flex-1">
                            <p className={`text-sm ${done ? "" : "text-[#eaeaea]"}`}>{a.title}</p>
                            {a.description && (
                              <p className="text-xs text-[#888] mt-1">{a.description}</p>
                            )}
                            {done && stamp && (
                              <p
                                className="text-[10px] font-mono uppercase tracking-widest text-[#c89f45] mt-1.5"
                                data-testid={`minutes-action-signoff-${a.id}`}
                              >
                                Signed off · {stamp.toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </p>
                            )}
                            {!done && a.status === "pending" && (
                              <p className="text-[10px] font-mono uppercase tracking-widest text-[#666] mt-1.5">
                                Pending sign-off
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>

            {meeting.recording_path && (
              <div className="surface rounded-md p-6" data-testid="recording-block">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="label-eyebrow">Recording</p>
                    <h3 className="font-serif text-xl mt-2 flex items-center gap-2"><Video size={16} className="text-[#c89f45]" /> Session playback</h3>
                  </div>
                  {isOwner && !meeting.transcript && transcribing && (
                    <div
                      className="flex items-center gap-2 text-xs text-[#c89f45] font-mono"
                      data-testid="auto-transcribing-indicator"
                    >
                      <span className="h-2 w-2 rounded-full bg-[#c89f45] animate-pulse" />
                      TRANSCRIBING · WHISPER-1
                    </div>
                  )}
                  {isOwner && !meeting.transcript && !transcribing && (
                    <Button
                      onClick={transcribeAndRedraft}
                      disabled={transcribing || generating}
                      variant="outline"
                      className="bg-transparent border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] rounded-md h-9"
                      data-testid="transcribe-and-redraft-button"
                    >
                      <Mic size={14} className="mr-1.5" />
                      Transcribe & redraft
                    </Button>
                  )}
                </div>
                <video
                  controls
                  preload="metadata"
                  playsInline
                  className="w-full rounded-md border border-[#2a2a2a] bg-black"
                  src={recordingSrc || undefined}
                  data-testid="recording-video"
                >
                  {recordingSrc && (
                    <a
                      href={recordingSrc}
                      download={`boardroom-${meeting.id}.webm`}
                      className="text-sm text-[#c89f45] underline"
                    >
                      Your browser can't play this recording — download it here.
                    </a>
                  )}
                </video>
                <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-[#666]">
                  <span>SESSION PLAYBACK</span>
                  {recordingSrc && (
                    <a
                      href={recordingSrc}
                      download={`boardroom-${meeting.id}.webm`}
                      className="hover:text-[#c89f45] transition-colors inline-flex items-center gap-1"
                      data-testid="recording-download"
                    >
                      <Download size={10} />
                      DOWNLOAD
                    </a>
                  )}
                </div>
                {meeting.transcript && (
                  <div className="mt-4 border-t border-[#1a1a1a] pt-4" data-testid="transcript-block">
                    <button
                      onClick={() => setTranscriptOpen((v) => !v)}
                      className="flex items-center justify-between w-full text-left group"
                      data-testid="transcript-toggle"
                    >
                      <div>
                        <p className="label-eyebrow">Transcript</p>
                        <p className="text-xs text-[#666] mt-1 font-mono">
                          {meeting.transcript_obj?.segments?.length
                            ? `${meeting.transcript_obj.segments.length} SEGMENTS · WHISPER-1`
                            : "WHISPER-1"}
                        </p>
                      </div>
                      <span className="text-[#888] group-hover:text-[#c89f45] transition-colors">
                        {transcriptOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>
                    {transcriptOpen && (
                      <div className="mt-4 max-h-[420px] overflow-y-auto pr-2" data-testid="transcript-body">
                        {meeting.transcript_obj?.segments?.length ? (
                          <ul className="space-y-3">
                            {meeting.transcript_obj.segments.map((s, i) => (
                              <li key={`seg-${i}-${s.start ?? ""}`} className="flex gap-3 text-sm" data-testid={`segment-${i}`}>
                                <span className="font-mono text-[10px] text-[#c89f45] mt-1 shrink-0">
                                  {formatTimestamp(s.start)}
                                </span>
                                <span className="text-[#ddd] leading-relaxed">{s.text.trim()}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-[#ddd] leading-relaxed whitespace-pre-wrap">{meeting.transcript}</p>
                        )}
                      </div>
                    )}
                    {isOwner && (
                      <button
                        onClick={transcribeAndRedraft}
                        disabled={transcribing || generating}
                        className="text-[10px] font-mono text-[#666] hover:text-[#c89f45] mt-4 transition-colors"
                        data-testid="retranscribe-button"
                      >
                        {transcribing || generating ? "RE-TRANSCRIBING & DRAFTING…" : "RE-TRANSCRIBE & REDRAFT →"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {meeting.transcript && (
              <AskTranscript
                meetingId={meeting.id}
                initialQa={meeting.transcript_qa}
              />
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
